import {produce} from 'immer'
import type {DumpOptions} from 'js-yaml'
import {dump, load} from 'js-yaml'

import type {SerializedError} from '@reduxjs/toolkit'
import type {FetchBaseQueryError} from '@reduxjs/toolkit/query'
import omit from 'lodash/omit'

import type {BuildType, GetAllBuildTypesApiArg, GetBuildTypeApiArg} from '../../../../services/rest'
import type {PipelineId} from '../../../../types'
import {stringifyId} from '../../../../types'
import {extractErrorMessage} from '../../../../utils/extractError'
import {
  customAgentsNames,
  isCloud,
  jobCountHardLimit,
  jobCountSoftLimit,
} from '../../../../utils/featureToggles'
import type {NewIntegrationDto, NewVcsRootDto, VcsRootDto} from '../services/pipelinesApi.types'
import type {
  Dependency,
  PipelineDependency,
  PipelineDependencyDetails,
  PipelineDependencyId,
  JobRepositorySettings,
  PipelineSettings,
  RunsOn,
} from '../types'

const normalizeJobRepositoryEntry = (
  entry: unknown,
): Record<string, JobRepositorySettings> | null => {
  if (typeof entry === 'string') {
    return {[entry]: {enabled: true}}
  }
  if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
    const [repositoryReference, repositoryValue] = Object.entries(
      entry as Record<string, unknown>,
    )[0] ?? [null, null]
    if (!repositoryReference) {
      return null
    }
    if (repositoryValue == null) {
      return {[repositoryReference]: {enabled: true}}
    }
    if (typeof repositoryValue === 'string') {
      return {[repositoryReference]: {enabled: true, path: repositoryValue}}
    }
    if (typeof repositoryValue === 'object') {
      return {[repositoryReference]: repositoryValue as JobRepositorySettings}
    }
  }
  return null
}

export const parsePipeline = (yaml: string): PipelineSettings => {
  const parsedPipeline = load(yaml) as PipelineSettings

  if (
    parsedPipeline?.['import-parameters'] != null &&
    !Array.isArray(parsedPipeline['import-parameters'])
  ) {
    parsedPipeline['import-parameters'] = []
  }

  if (!parsedPipeline?.jobs) {
    return parsedPipeline
  }

  // TW-95254 Custom deserialization for integrations
  for (const [_, job] of Object.entries(parsedPipeline.jobs)) {
    if (job?.integrations && job.integrations.length > 0) {
      job.integrations = job.integrations
        .map(integration => Object.entries(integration)[0])
        .filter(integration => integration.length === 2)
        .map(integration => ({
          id: integration[1],
          type: integration[0],
        }))
    }
    if (job?.repositories && job.repositories.length > 0) {
      job.repositories = job.repositories
        .map(repository => normalizeJobRepositoryEntry(repository))
        .filter(
          (repository): repository is Record<string, JobRepositorySettings> => repository != null,
        )
    }
  }

  return parsedPipeline
}

export const getVcsRootReference = (vcsRoot?: NewVcsRootDto | VcsRootDto) => {
  if (!vcsRoot) {
    return undefined
  }
  if (vcsRoot.isExternal) {
    return vcsRoot.externalVcsRootId ?? vcsRoot.id
  }
  return vcsRoot.id
}

export const stringifyPipeline: (pipeline: PipelineSettings, opts?: DumpOptions) => string = dump

export const getDependencyName = (dependency: Dependency) =>
  typeof dependency === 'string' ? dependency : Object.keys(dependency)[0]

export const getPipelinesSettingsWithoutDeprecatedFields = (settings: PipelineSettings) =>
  omit(settings, ['name'])

export const getPreformattingSettings = (key: string, value: any) => {
  const isJobsSection = key === 'jobs'
  const isValueString = typeof value === 'string'
  const isIntegrationsSection = key === 'integrations'
  const isImportedParametersSection = key === 'importParameters'

  if (isValueString) {
    return value.trim()
  }

  if (isJobsSection && typeof value === 'object') {
    return Object.keys(value).reduce((accumulator, jobName) => {
      const jobValue = value[jobName]
      const jobNameFormatted = jobName.trim()

      return {
        ...accumulator,
        [jobNameFormatted]: jobValue,
      }
    }, {})
  }

  if (isIntegrationsSection && Array.isArray(value) && value.length > 0) {
    return value
      .filter(integration => integration.type && integration.id)
      .map(integration => ({
        [`${integration.type}`]: integration.id,
      }))
  }

  if (isImportedParametersSection) {
    return value
  }

  return value
}

export const getPreformattingIntegrations = (draft: NewIntegrationDto[]) => {
  draft?.forEach(({parameters}) => {
    for (const [key, value] of Object.entries(parameters)) {
      if (value == null) {
        delete parameters[key]
      }
    }
  })
}

const getPreformattingVcsRoot = (draft?: NewVcsRootDto) => {
  if (!draft) {
    return
  }

  if (draft.isExternal) {
    draft.externalVcsRootId = draft.id
  }
  if (draft.isNew && draft.externalVcsRootId == null && draft.id) {
    draft.externalVcsRootId = draft.id
  }

  draft.statusPublishingEnabled = draft.isStatusPublishingEnabled
  draft.pullRequestsTrackingEnabled = draft.trackPullRequests
}

const getPreformattingVcsRoots = (draft?: NewVcsRootDto[]) => {
  draft?.forEach(getPreformattingVcsRoot)
}

export function preformatPipelineVcsData<T extends NewVcsRootDto>(data: {
  vcsRoot: T
  additionalVcsRoots: T[]
}): {vcsRoot: T; additionalVcsRoots: T[]} {
  return {
    vcsRoot: produce(data.vcsRoot, getPreformattingVcsRoot),
    additionalVcsRoots: produce(data.additionalVcsRoots, getPreformattingVcsRoots),
  }
}

export const getCustomAgents = (): Array<{name: string}> => {
  const customAgentsProp = customAgentsNames || null
  return customAgentsProp
    ? customAgentsProp
        .split(',')
        .filter(Boolean)
        .map((i: string) => ({name: i.trim()}))
    : []
}

export const jobLimits = {
  soft: jobCountSoftLimit,
  hard: jobCountHardLimit,
}

export const getExtractErrorMessages = (
  error: SerializedError | FetchBaseQueryError | string | null | undefined,
) => {
  try {
    if (error != null) {
      const extractedError = extractErrorMessage(error)

      if (typeof extractedError === 'string') {
        const errors = JSON.parse(extractedError).errors

        if (Array.isArray(errors)) {
          return errors
        }
      }
    }
  } catch (e) {
    return null
  }

  return null
}

export function isSelfHostedRunsOn(runsOn: RunsOn | undefined): boolean {
  if (!isCloud) {
    return true // On-premises TeamCity supports only self-hosted agents, so we can consider any runsOn value as self-hosted
  }

  return typeof runsOn === 'object' ? 'self-hosted' in runsOn : runsOn === 'self-hosted'
}

export const getDependencyDetails = (
  pipelineId: PipelineId,
  dependencyId: PipelineDependencyId,
  buildType: BuildType,
): {
  pipelineId: PipelineId
  dependencyId: PipelineDependencyId
  value: PipelineDependencyDetails
} => {
  const {name, project} = buildType
  const {holdsPipeline: isPipeline, parentProjectId, ancestorProjects} = project ?? {}
  const pipelineParentProjectId = isPipeline
    ? ancestorProjects?.project?.find(({id}) => id === parentProjectId)?.parentProjectId
    : null
  return {
    pipelineId,
    dependencyId,
    value: {
      name: isPipeline ? project?.name : name,
      projectId: isPipeline ? parentProjectId : project?.id,
      parentProjectId: pipelineParentProjectId ?? parentProjectId,
      isPipeline,
    },
  }
}

export const getDependencyArgs = (id: PipelineDependencyId): GetBuildTypeApiArg => ({
  btLocator: `id:${stringifyId(id)}`,
  fields:
    'id,name,project(id,name,parentProjectId,archived,holdsPipeline,ancestorProjects(project(id,name,parentProjectId,archived)))',
})

export const getUpstreamDependenciesArgs = (pipelineId: PipelineId): GetAllBuildTypesApiArg => ({
  locator: `snapshotDependency(to:(id:${stringifyId(pipelineId)}))`,
  fields:
    'buildType(id,name,project(id,name,parentProjectId,archived,holdsPipeline,ancestorProjects(project(id,name,parentProjectId,archived))))',
})

export const getPipelineDependencyId = (dependency: PipelineDependency) =>
  typeof dependency === 'string' ? dependency : (Object.keys(dependency)[0] as PipelineDependencyId)

export const getPipelineDependency = (dependency: PipelineDependency, dependencyId: string) =>
  typeof dependency === 'string'
    ? dependency === dependencyId
    : dependency[dependencyId as PipelineDependencyId]
